import React, { useState, useMemo, useRef, useEffect } from 'react';
import { formatTime } from '../Utils/flightHelpers';
import { AIRPORT_COORDS } from '../Constants/airportCoords';

const StatCell = ({ label, value, color, locked, onLockedClick, badge, onBadgeClick, statKey, explanation, expanded, onStatClick, isPremium }) => {
  return (
    <div
      className={`sia-route-stat${locked ? ' locked' : ''}`}
      onClick={locked ? onLockedClick : () => onStatClick(statKey)}
    >
      <div className="sia-route-stat-label">
        {label}
        {locked && (
          <>
            <span className="sia-lock-pip">
              <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            </span>
            <span className="sia-premium-label">Pro</span>
          </>
        )}
        {badge && !locked && !expanded && (
          <button
            style={{ marginLeft: 'auto', fontSize: '0.58rem', background: '#f0efeb', border: 'none', borderRadius: '3px', padding: '1px 5px', cursor: 'pointer', color: 'var(--gray-5)', fontFamily: 'DM Sans, sans-serif' }}
            onClick={e => { e.stopPropagation(); onBadgeClick(); }}
          >{badge}</button>
        )}
      </div>
      {expanded ? (
        <div style={{ fontSize: '0.65rem', color: 'var(--gray-5)', marginTop: '4px', lineHeight: 1.4 }}>{explanation}</div>
      ) : (
        <div className={`sia-route-stat-val${color === 'teal' ? ' teal' : ''}${locked ? ' blurred' : ''}`}>
          {locked ? '—' : value}
        </div>
      )}
    </div>
  );
};

const StatusBar = ({ statusBreakdown }) => {
  const total = Object.values(statusBreakdown).reduce((a, b) => a + b, 0);
  if (total === 0) return null;

  const colorMap = {
    arrived: '#22c55e',
    departed: '#0891b2',
    delayed: '#f59e0b',
    diverted: '#e31837',
  };
  const defaultColor = '#9b9891';

  const entries = Object.entries(statusBreakdown);

  return (
    <div>
      <div className="sia-status-bar-track">
        {entries.map(([status, count]) => (
          <div
            key={status}
            className="sia-bar-seg"
            style={{ width: `${(count / total) * 100}%`, background: colorMap[status.toLowerCase()] || defaultColor }}
          />
        ))}
      </div>
      <div className="sia-status-legend">
        {entries.map(([status, count]) => (
          <div key={status} className="sia-legend-item">
            <span className="sia-legend-dot" style={{ background: colorMap[status.toLowerCase()] || defaultColor }} />
            <span style={{ textTransform: 'capitalize' }}>{status}</span>
            <span className="sia-legend-num">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const FlightRow = ({ flight }) => {
  const colorMap = { arrived: '#22c55e', departed: '#0891b2', delayed: '#f59e0b', diverted: '#e31837' };
  const statusColor = colorMap[flight.status?.toLowerCase()] || '#9b9891';

  return (
    <div className="sia-flight-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
        <div>
          <span className="sia-flight-num">{flight.flightNumber}</span>
          <span className="sia-flight-call">{flight.callSign}</span>
        </div>
        <span
          className="sia-status-pill"
          style={{ color: statusColor, background: `${statusColor}18` }}
        >{flight.status}</span>
      </div>
      <div className="sia-flight-meta">
        {flight.aircraft} · {flight.registration}
      </div>
      <div className="sia-flight-meta" style={{ marginTop: '2px' }}>
        {formatTime(flight.scheduledDep)} → {formatTime(flight.scheduledArr)}
        {flight.terminal && ` · T${flight.terminal}`}
        {flight.gate && ` G${flight.gate}`}
      </div>
    </div>
  );
};

const RouteDetails = ({ route, onClose, isPremium, onPremiumFeatureClick, onModelClick }) => {
  const [showAllFlights, setShowAllFlights] = useState(false);
  const [distanceUnit, setDistanceUnit] = useState('km'); // 'km' or 'mi'
  const [expandedStats, setExpandedStats] = useState({}); // Track which stats are expanded
  const timeoutsRef = useRef({}); // Use ref to store timeouts

  // Separate flights by direction if bidirectional
  const flightsByDirection = useMemo(() => {
    if (!route) return { single: [] };

    if (!route.isBidirectional) {
      return { single: route.flights };
    }

    const forward = [];
    const reverse = [];

    route.flights.forEach(flight => {
      if (flight.origin === route.origin && flight.destination === route.destination) {
        forward.push(flight);
      } else {
        reverse.push(flight);
      }
    });

    return { forward, reverse };
  }, [route]);

  // Clean up timeouts on unmount
  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  if (!route) return null;

  // Calculate route statistics
  const stats = {
    totalFlights: route.flights.length,
    uniqueAircraft: new Set(route.flights.map(f => f.registration)).size,
    uniqueAircraftModels: new Set(route.flights.map(f => f.aircraft)).size,
    aircraftModels: [...new Set(route.flights.map(f => f.aircraft))],
    scheduledAvgDuration: calculateScheduledAvgDuration(route.flights),
    actualAvgDuration: calculateActualAvgDuration(route.flights),
    dayNightRatio: calculateDayNightRatio(route.flights),
    routeDistance: calculateRouteDistance(route, distanceUnit),
    avgFlightsPerDay: calculateAvgFlightsPerDay(route.flights),
    statusBreakdown: getStatusBreakdown(route.flights)
  };

  // Stat explanations
  const statExplanations = {
    totalFlights: "Total number of flights on this route in the dataset",
    uniqueAircraft: "Number of unique aircraft (by registration) that flew this route",
    uniqueAircraftModels: "Number of unique aircraft models/types used on this route",
    routeDistance: `Great circle distance between airports in ${distanceUnit === 'km' ? 'kilometers' : 'miles'}`,
    scheduledAvgDuration: "Average scheduled flight duration from departure to arrival",
    actualAvgDuration: "Average actual flight time based on real departure and arrival times",
    dayNightRatio: "Ratio of daytime (6am-6pm) to nighttime departures",
    avgFlightsPerDay: "Average number of flights per day across all dates in dataset"
  };

  // Additional stats for bidirectional routes
  const bidirectionalStats = route.isBidirectional ? {
    forwardCount: flightsByDirection.forward.length,
    reverseCount: flightsByDirection.reverse.length
  } : null;

  const toggleDistanceUnit = () => {
    setDistanceUnit(prev => prev === 'km' ? 'mi' : 'km');
  };

  const handleShowFlightsClick = () => {
    if (!isPremium) {
      onPremiumFeatureClick();
    } else {
      setShowAllFlights(!showAllFlights);
    }
  };

  const handleAircraftModelClick = (modelName) => {
    if (isPremium && onModelClick) {
      console.log('Model clicked:', modelName);
      onModelClick(modelName);
    } else if (!isPremium) {
      onPremiumFeatureClick();
    }
  };

  const handleStatClick = (statKey) => {
    if (expandedStats[statKey]) {
      // Collapse this stat
      if (timeoutsRef.current[statKey]) {
        clearTimeout(timeoutsRef.current[statKey]);
        delete timeoutsRef.current[statKey];
      }
      setExpandedStats(prev => {
        const newStats = { ...prev };
        delete newStats[statKey];
        return newStats;
      });
    } else {
      // Expand this stat
      setExpandedStats(prev => ({ ...prev, [statKey]: true }));

      // Auto-collapse after 3 seconds
      const timeout = setTimeout(() => {
        setExpandedStats(prev => {
          const newStats = { ...prev };
          delete newStats[statKey];
          return newStats;
        });
        delete timeoutsRef.current[statKey];
      }, 2500);

      timeoutsRef.current[statKey] = timeout;
    }
  };

  return (
    <div
      className="absolute top-16 right-4 z-[1000] overflow-y-auto [scrollbar-width:none] sia-route-panel"
      style={{ width: 'min(95vw, 310px)', maxHeight: 'calc(100dvh - 5rem)' }}
      onWheel={e => e.stopPropagation()}
      onTouchMove={e => e.stopPropagation()}
    >
      {/* Header */}
      <div className="sia-route-header">
        <button className="sia-route-close" onClick={onClose}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <div className="sia-route-code">
          {route.origin} <span className="sia-route-arrow">{route.isBidirectional ? '↔' : '→'}</span> {route.destination}
        </div>
        <div className="sia-teal-bar" />
        <div className="sia-route-sub">
          {route.originName} {route.isBidirectional ? '↔' : '→'} {route.destinationName}
        </div>
      </div>

      {/* Stats grid */}
      <div className="sia-stats-grid-2">
        {/* Free stat */}
        <StatCell label="Flights" value={stats.totalFlights} color="teal"
          statKey="totalFlights" explanation={statExplanations.totalFlights}
          expanded={expandedStats.totalFlights} onStatClick={handleStatClick} />
        {/* Free stat */}
        <StatCell label="Aircraft" value={stats.uniqueAircraft}
          statKey="uniqueAircraft" explanation={statExplanations.uniqueAircraft}
          expanded={expandedStats.uniqueAircraft} onStatClick={handleStatClick} />
        {/* Free stat */}
        <StatCell label="Models" value={stats.uniqueAircraftModels}
          statKey="uniqueAircraftModels" explanation={statExplanations.uniqueAircraftModels}
          expanded={expandedStats.uniqueAircraftModels} onStatClick={handleStatClick} />
        {/* Premium */}
        <StatCell label="Distance" value={`${stats.routeDistance} ${distanceUnit.toUpperCase()}`}
          locked={!isPremium} onLockedClick={onPremiumFeatureClick}
          badge={distanceUnit.toUpperCase()} onBadgeClick={toggleDistanceUnit}
          statKey="routeDistance" explanation={statExplanations.routeDistance}
          expanded={expandedStats.routeDistance} onStatClick={handleStatClick} isPremium={isPremium} />
        {/* Free */}
        <StatCell label="Sched. Duration" value={stats.scheduledAvgDuration}
          statKey="scheduledAvgDuration" explanation={statExplanations.scheduledAvgDuration}
          expanded={expandedStats.scheduledAvgDuration} onStatClick={handleStatClick} />
        {/* Premium */}
        <StatCell label="Actual Duration" value={stats.actualAvgDuration}
          locked={!isPremium} onLockedClick={onPremiumFeatureClick}
          statKey="actualAvgDuration" explanation={statExplanations.actualAvgDuration}
          expanded={expandedStats.actualAvgDuration} onStatClick={handleStatClick} isPremium={isPremium} />
        {/* Premium */}
        <StatCell label="Day/Night" value={stats.dayNightRatio}
          locked={!isPremium} onLockedClick={onPremiumFeatureClick}
          statKey="dayNightRatio" explanation={statExplanations.dayNightRatio}
          expanded={expandedStats.dayNightRatio} onStatClick={handleStatClick} isPremium={isPremium} />
        {/* Premium */}
        <StatCell label="Avg/Day" value={stats.avgFlightsPerDay}
          locked={!isPremium} onLockedClick={onPremiumFeatureClick}
          statKey="avgFlightsPerDay" explanation={statExplanations.avgFlightsPerDay}
          expanded={expandedStats.avgFlightsPerDay} onStatClick={handleStatClick} isPremium={isPremium} />
      </div>

      {/* Bidirectional breakdown */}
      {route.isBidirectional && bidirectionalStats && (
        <div className="sia-section-block">
          <div className="sia-section-label">Direction split</div>
          <div className="sia-direction-row">
            <span className="sia-direction-label">{route.origin} → {route.destination}</span>
            <span className="sia-direction-count">{bidirectionalStats.forwardCount}</span>
          </div>
          <div className="sia-direction-row">
            <span className="sia-direction-label">{route.destination} → {route.origin}</span>
            <span className="sia-direction-count">{bidirectionalStats.reverseCount}</span>
          </div>
        </div>
      )}

      {/* Aircraft models */}
      <div className="sia-section-block">
        <div className="sia-section-label">Aircraft models</div>
        {isPremium ? (
          <div className="sia-aircraft-pills">
            {stats.aircraftModels.map((model, idx) => (
              <button key={idx} className="sia-aircraft-pill" onClick={() => handleAircraftModelClick(model)}>
                {model}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ position: 'relative', cursor: 'pointer' }} onClick={onPremiumFeatureClick}>
            <div className="sia-aircraft-pills" style={{ filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none' }}>
              <span className="sia-aircraft-pill">A350-900</span>
              <span className="sia-aircraft-pill">B777-300ER</span>
            </div>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
              <span className="sia-lock-pip">
                <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </span>
              <span style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--red)' }}>Premium</span>
            </div>
          </div>
        )}
      </div>

      {/* Status breakdown */}
      <div className="sia-section-block">
        <div className="sia-section-label">Flight status</div>
        <StatusBar statusBreakdown={stats.statusBreakdown} />
      </div>

      {/* CTA */}
      <div className="sia-cta-section">
        <button
          className={`sia-cta-btn${!isPremium ? ' locked' : ''}`}
          onClick={handleShowFlightsClick}
        >
          {!isPremium && (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          )}
          {isPremium ? (showAllFlights ? '▾ Hide flights' : `Show all flights (${stats.totalFlights})`) : `Show all flights (${stats.totalFlights})`}
        </button>
      </div>

      {/* Flight list */}
      {showAllFlights && isPremium && (
        <div style={{ borderTop: '1px solid #f0efeb' }}>
          {route.isBidirectional ? (
            <>
              {flightsByDirection.forward.length > 0 && (
                <>
                  <div style={{ padding: '8px 13px 4px', fontSize: '0.65rem', fontWeight: 600, color: 'var(--gray-4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {route.origin} → {route.destination} ({flightsByDirection.forward.length})
                  </div>
                  {flightsByDirection.forward.map((f, i) => <FlightRow key={i} flight={f} />)}
                </>
              )}
              {flightsByDirection.reverse.length > 0 && (
                <>
                  <div style={{ padding: '8px 13px 4px', fontSize: '0.65rem', fontWeight: 600, color: 'var(--gray-4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {route.destination} → {route.origin} ({flightsByDirection.reverse.length})
                  </div>
                  {flightsByDirection.reverse.map((f, i) => <FlightRow key={i} flight={f} />)}
                </>
              )}
            </>
          ) : (
            route.flights.map((f, i) => <FlightRow key={i} flight={f} />)
          )}
        </div>
      )}
    </div>
  );
};

// Helper functions
function calculateScheduledAvgDuration(flights) {
  const durations = flights
    .filter(f => f.scheduledDep && f.scheduledArr)
    .map(f => {
      const dep = new Date(f.scheduledDep);
      const arr = new Date(f.scheduledArr);
      return (arr - dep) / (1000 * 60);
    })
    .filter(d => d > 0 && d < 1440);

  if (durations.length === 0) return 'N/A';

  const avg = durations.reduce((a, b) => a + b, 0) / durations.length;
  const hours = Math.floor(avg / 60);
  const mins = Math.round(avg % 60);
  return `${hours}h ${mins}m`;
}

function calculateActualAvgDuration(flights) {
  const durations = flights
    .filter(f => f.actualDep && f.actualArr)
    .map(f => {
      const dep = new Date(f.actualDep);
      const arr = new Date(f.actualArr);
      return (arr - dep) / (1000 * 60);
    })
    .filter(d => d > 0 && d < 1440);

  if (durations.length === 0) return 'N/A';

  const avg = durations.reduce((a, b) => a + b, 0) / durations.length;
  const hours = Math.floor(avg / 60);
  const mins = Math.round(avg % 60);
  return `${hours}h ${mins}m`;
}

function calculateRouteDistance(route, unit = 'km') {
  const origin = AIRPORT_COORDS[route.origin];
  const dest = AIRPORT_COORDS[route.destination];

  if (!origin || !dest) return 'N/A';

  const [originLat, originLon] = origin;
  const [destLat, destLon] = dest;

  const R = 6371;
  const dLat = (destLat - originLat) * Math.PI / 180;
  const dLon = (destLon - originLon) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(originLat * Math.PI / 180) * Math.cos(destLat * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distanceKm = R * c;

  if (unit === 'mi') {
    const distanceMi = distanceKm * 0.621371;
    return Math.round(distanceMi).toLocaleString();
  }

  return Math.round(distanceKm).toLocaleString();
}

function calculateAvgFlightsPerDay(flights) {
  const dates = flights
    .map(f => f.scheduledDep)
    .filter(d => d)
    .map(d => new Date(d).toISOString().split('T')[0]);

  if (dates.length === 0) return 'N/A';

  const uniqueDates = new Set(dates);
  const avg = flights.length / uniqueDates.size;

  return avg >= 10 ? Math.round(avg) : avg.toFixed(1);
}

function calculateDayNightRatio(flights) {
  const flightsWithTime = flights.filter(f => f.scheduledDep);
  if (flightsWithTime.length === 0) return 'N/A';

  let dayFlights = 0;
  let nightFlights = 0;

  flightsWithTime.forEach(f => {
    const depTime = new Date(f.scheduledDep);
    const hour = depTime.getHours();

    if (hour >= 6 && hour < 18) {
      dayFlights++;
    } else {
      nightFlights++;
    }
  });

  if (dayFlights === 0 && nightFlights === 0) return 'N/A';

  if (nightFlights === 0) return `${dayFlights}:0`;
  if (dayFlights === 0) return `0:${nightFlights}`;

  const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
  const divisor = gcd(dayFlights, nightFlights);

  return `${Math.round(dayFlights/divisor)}:${Math.round(nightFlights/divisor)}`;
}

function getStatusBreakdown(flights) {
  const breakdown = {};
  flights.forEach(f => {
    const status = f.status || 'Unknown';
    breakdown[status] = (breakdown[status] || 0) + 1;
  });
  return breakdown;
}

export default RouteDetails;
